import React from "react";
import { useTranslation } from "react-i18next";
import { Field, FieldInputProps } from "react-final-form";

// Primereact
import { Password } from "primereact/password";
import { ObjectUtils, classNames } from "primereact/utils";

// Own "library"
import UiFieldLabel from "../field-label/FieldLabel.component";
import { UiInputPasswordProps } from "../../models/components/input-password.model";
import UiFieldErrors from "../field-errors/FieldErrors.component";
import { Validations } from "../../services/form/validations.service";

const UiInputPassword = (props: UiInputPasswordProps) => {
	const { t } = useTranslation("common");

	// Props that need special treatment (like the placholder that needs to be translated)
	const otherProps = ObjectUtils.findDiffKeys(props, {
		placeholder: props?.placeholder,
		leftIcon: props.leftIcon,
		leftIconFn: props.leftIconFn,
		className: props.className,
		onChange: props.onChange,
		onBlur: props.onBlur,
		name: props.name,
		value: props.value,
		toggleMask: props.toggleMask,
		feedback: props.feedback,
		removeBottomSpacer: props.removeBottomSpacer,
	});

	// Use a custom onChange function passed as a prop, and integrate it with
	const handleChange = (
		event: React.ChangeEvent<HTMLInputElement>,
		input: FieldInputProps<any, HTMLElement>
	) => {
		/**
		 * Force onBlur propagation (final-form library):
		 * meta.touched is only updated with the onBlur event, but we need it to be updated also with the onChange event
		 */
		if (input) {
			input.onChange(event);
			input.onBlur();
		}
		// Execute onChange callback function
		if (props.onChange) props.onChange(event);
	};

	const handleBlur = (
		event: React.FocusEvent<HTMLInputElement, Element>,
		input: FieldInputProps<any, HTMLElement>
	): void => {
		if (input) input.onBlur();
		// Execute onBlur callback function
		if (props.onBlur) props.onBlur(event);
	};

	return (
		<Field
			name={props.name}
			validate={Validations.composeValidators(props.validations)}
			render={({ input, meta }) => (
				<div
					className={classNames("k-input-container", props.removeBottomSpacer && "mb-0")}
				>
					<div className="k-inputpassword-container">
						<UiFieldLabel
							id={props.id}
							label={props.label}
							disabled={props.disabled}
							leftIcon={props.leftIcon}
							meta={meta}
							validations={props.validations}
						>
							{/* Left icon */}
							{props.leftIcon && (
								<i
									className={classNames(
										"input-icon",
										props.leftIcon,
										props.leftIconFn ? "is-clickable" : "",
										props.disabled ? "p-disabled" : ""
									)}
									onClick={props.leftIconFn}
									onKeyDown={(e) => {
										if (e.key === "Enter" || e.key === " ") {
											e.preventDefault();
											props.leftIconFn?.();
										}
									}}
									role="button"
									tabIndex={0}
								/>
							)}

							{/* Input */}
							<Password
								placeholder={props?.placeholder ? t(props.placeholder) : undefined}
								className={classNames(
									{ "p-invalid": !!(meta.touched && meta.error) },
									props.className
								)}
								name={input.name}
								value={input.value}
								onChange={(event) => handleChange(event, input)}
								onBlur={(event) => handleBlur(event, input)}
								toggleMask={props.toggleMask !== false}
								feedback={props.feedback || false}
								data-testid={props.testId}
								{...otherProps}
							/>
						</UiFieldLabel>
					</div>

					{/* Validation messages */}
					<UiFieldErrors {...meta}></UiFieldErrors>
				</div>
			)}
		/>
	);
};

export default UiInputPassword;
